import TableList from "../../../components/Tools/TableList";
import { useState, useEffect, useCallback } from "react";
import { apiService } from "../../../components/Config/api.service";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { FiDownload } from 'react-icons/fi';

const Bulletins = ({ payslips }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState({}); 
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 5,
        total_pages: 1,
        total_count: 0
    });

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;

        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        if (newFilterBy !== filterBy) {
            setFilterBy(newFilterBy);
        }
        
    };

    const downloadPayslipPDF = (payslipId) => {
        apiService.getBlobFile(`/payslip/pdf/${payslipId}`)
            .then(response => {
                if (response && response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `bulletin_${payslipId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            })
            .catch(error => {
                console.error('Erreur lors du téléchargement du PDF:', error);
                alert("Erreur lors du téléchargement du PDF");
            });
    };

    const columns = [
        {
            key: 'Download',
            label: ' ',
            render: (item) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        downloadPayslipPDF(item.id);
                    }}
                    className="text-[--color-primary] hover:text-[--color2]"
                >
                    <FiDownload className="text-lg" aria-label="download" />
                </button>
            )
        },
        { key: 'Employee', label: 'Employé' },
        {
            key: 'State',
            label: 'État',
            render: (item) => {
                let badgeColor;
                switch (item.State.toLowerCase()) {
                    case 'draft':
                        badgeColor = 'bg-yellow-100 text-yellow-800';
                        break;
                    case 'verify':
                        badgeColor = 'bg-blue-100 text-blue-800';
                        break;
                    case 'done':
                        badgeColor = 'bg-green-100 text-green-800';
                        break;
                    case 'refused':
                        badgeColor = 'bg-red-100 text-red-800';
                        break;
                    default:
                        badgeColor = 'bg-gray-100 text-gray-800';
                }

                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                        {item.State === 'draft' ? 'Brouillon' :
                            item.State === 'verify' ? 'À vérifier' :
                                item.State === 'done' ? 'Validé' :
                                    item.State === 'refused' ? 'Refusé' : item.State}
                    </span>
                );
            }
        },
        {
            key: 'Period',
            label: 'Mois',
            render: (item) => {
                const dateFrom = new Date(item.De);
                return dateFrom.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            }
        },
        {
            key: 'gross_salary',
            label: ' Brut',
            render: (item) => `${Number(item.gross_salary).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'net_salary',
            label: ' Net',
            render: (item) => `${Number(item.net_salary).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'Cotisations Salariales',
            label: 'C Salarial',
            render: (item) => `${Number(item.cotisation_salariale).toLocaleString('fr-FR')} CFA`
        },
        {
            key: 'Cotisations Patronales',
            label: 'C Patronal',
            render: (item) => `${Number(item.cotisation_patronale).toLocaleString('fr-FR')} CFA`
        }
    ];
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(() =>
        new Date().toISOString().slice(0, 7)
    );

    const handleMonthChange = (e) => {
        const month = e.target.value;
        setSelectedMonth(month);
    };

    const fetchPdf = useCallback(
        async (id) => {
            if (!id) {
                setErrorMessage("ID du bulletin non fourni.");
                return;
            }

            try {
                setIsLoading(true);
                const response = await apiService.getBlobFile(`/payslip/pdf/${id}`);
                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/pdf" })
                );
                setPdfUrl(url);
            } catch (error) {
                console.error("Error downloading PDF:", error);
                setErrorMessage("Erreur lors du téléchargement du PDF.");
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        if (payslips?.length > 0) {
            const currentPayslip = payslips.find((p) =>
                p.De.startsWith(selectedMonth)
            );
            if (currentPayslip) {
                fetchPdf(currentPayslip.id);
            } else {
                setPdfUrl("");
                setErrorMessage("Aucun bulletin disponible pour ce mois.");
            }
        }
    }, [selectedMonth, payslips, fetchPdf]);

    return (
        <div className="p-6 grid grid-cols-1 md:grid-cols-1 gap-6">
            <div className="bg-white flex mb-4 p-4 border rounded">
                <div className="w-full bg-white p-4 min-h-screen">
                    <div className="mb-4 flex justify-between items-center">
                        <div>
                            <label className="block mb-2 font-bold">Mois :</label>
                            <input
                                type="month"
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                className="border py-2 px-4"
                            />
                        </div>
                        <div>
                            <a
                                href={pdfUrl ? pdfUrl : "#"}
                                className={`btn rounded-full text-white ${
                                    pdfUrl
                                        ? "bg-[--color-primary] hover:bg-[--color-primary]"
                                        : "bg-gray-400 cursor-not-allowed"
                                }`}
                                download
                            >
                               <span className="mr-1">🖨️</span> Imprimer
                            </a>
                        </div>
                    </div>
                    <hr className="py-1 mb-4" />

                    {/* Affichage du PDF */}
                    {isLoading ? (
                        <div className="h-[800px] flex items-center justify-center">
                            <div className="animate-pulse w-full">
                                <div className="h-6 bg-gray-300 rounded mb-4"></div>
                                <div className="h-6 bg-gray-300 rounded mb-4"></div>
                                <div className="h-[700px] bg-gray-300 rounded"></div>
                            </div>
                        </div>
                    ) : pdfUrl ? (
                        <div className="h-[800px]">
                            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                <Viewer fileUrl={pdfUrl} />
                            </Worker>
                        </div>
                    ) : (
                        <p className="text-center text-gray-500">
                            {errorMessage || "Aucun Bulletin pour ce mois"}
                        </p>
                    )}
                </div>
            </div>
            {/* <div className="bg-gray-50 p-4 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-2">Historique des Bulletins</h3>
                <TableList
                    columns={columns}
                    data={payslips}
                    model={'payslip'}
                    searchQuery={searchTerm}
                    setSearchQuery={setSearchTerm}
                    filterBy={filterBy}
                    setFilterBy={setFilterBy}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                />
            </div> */}
        </div>
    );
};

export default Bulletins;